import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, ChevronDown, Menu, Sun, Moon } from 'lucide-react'
import { cn } from '../../utils/cn'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { LanguageSwitcher } from '../ui/LanguageSwitcher'
import { NotificationBell } from '../notifications/NotificationBell'
import { FINANCIAL_NAV_ROUTES } from './financialNavRoutes'
import { SALES_NAV_ROUTES } from './salesNavRoutes'
import { useAccessControl } from '../../contexts/AccessControlContext'
import { getNavKeyForHref } from '../../config/navigationCatalog'

interface ModernHeaderProps {
  className?: string
  onMenuOpen?: () => void
}

interface NavChild {
  label: string
  href: string
  matchNested?: boolean
}

interface NavItemConfig {
  label: string
  href: string
  exact?: boolean
  children?: NavChild[]
}

function isDropdownChildActive(pathname: string, href: string, matchNested?: boolean): boolean {
  if (href === '/financial' && !matchNested) {
    return pathname === '/financial'
  }
  if (matchNested) {
    return pathname === href || pathname.startsWith(`${href}/`)
  }
  return pathname === href
}

export const ModernHeader: React.FC<ModernHeaderProps> = ({ className = '', onMenuOpen }) => {
  const { t } = useTranslation()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const { user, logout } = useAuth()
  const { canAccess, isReady } = useAccessControl()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const profileRef = useRef<HTMLDivElement>(null)

const navItems: NavItemConfig[] = useMemo(
    () => [
      { label: t('nav.dashboard'), href: '/', exact: true },
      {
        label: t('nav.vehicles'),
        href: '/vehicles',
        children: [
          { label: t('nav.vehicles'), href: '/vehicles' },
          { label: t('nav.newVehicle'), href: '/vehicles/new' },
        ],
      },
      {
        label: t('nav.leads'),
        href: '/leads',
        children: [
          { label: t('nav.allLeads'), href: '/leads' },
          { label: t('nav.leadsSub.report'), href: '/leads/report' },
          { label: t('nav.conversations'), href: '/leads/conversations' },
          { label: t('nav.leadsSub.distribution'), href: '/leads/settings/distribution' },
          { label: t('nav.settings'), href: '/leads/settings' },
        ],
      },
      {
        label: t('nav.sales'),
        href: '/sales',
        children: [
          { label: t('nav.salesSub.closedDeals'), href: '/financial/sales', matchNested: true },
          { label: t('nav.salesSub.acceptedProposals'), href: '/proposals', matchNested: true },
        ],
      },
      {
        label: t('nav.marketplace'),
        href: '/marketplace',
        children: [
          { label: t('nav.marketplaceSub.integrations'), href: '/marketplace/integrations', matchNested: true },
          { label: t('nav.marketplaceSub.publications'), href: '/marketplace/publications', matchNested: true },
          { label: t('nav.marketplaceSub.history'), href: '/marketplace/history', matchNested: true },
        ],
      },
      {
        label: t('nav.financial'),
        href: '/financial',
        children: FINANCIAL_NAV_ROUTES.map((r) => ({
          label: t(`nav.financialSub.${r.labelKey}`),
          href: r.href,
          matchNested: r.matchNested,
        })),
      },
      {
        label: t('nav.postSales'),
        href: '/post-sales/pipeline',
        children: [
          {
            label: t('nav.postSalesSub.salesPipeline'),
            href: '/post-sales/pipeline',
            matchNested: true,
          },
          {
            label: t('nav.postSalesSub.preparationPipeline'),
            href: '/operation?tab=preparation',
            matchNested: true,
          },
          {
            label: t('nav.postSalesSub.deliveries'),
            href: '/operation?tab=deliveries',
            matchNested: true,
          },
          {
            label: t('nav.postSalesSub.postReceipt'),
            href: '/operation?tab=receipt',
            matchNested: true,
          },
        ],
      },
      {
        label: t('nav.management'),
        href: '/settings',
        children: [
          { label: t('nav.maintenance'), href: '/maintenance', matchNested: true },
          { label: t('nav.users'), href: '/users', matchNested: true },
          { label: t('nav.settingsGeneral'), href: '/settings', matchNested: true },
          { label: t('nav.settingsTheme'), href: '/settings/theme', matchNested: true },
          { label: t('nav.settingsDealership'), href: '/settings/dealership', matchNested: true },
          { label: t('nav.settingsSub.billing'), href: '/settings/billing', matchNested: true },
          { label: t('nav.settingsPaymentForms'), href: '/settings/payment-forms', matchNested: true },
          { label: t('nav.settingsSub.roleAccess'), href: '/settings/role-access', matchNested: true },
        ],
      },
    ],
    [t]
  )

  const filteredNavItems = useMemo(() => {
    if (!isReady) {
      return []
    }
    return navItems
      .map(item => {
        const itemKey = getNavKeyForHref(item.href)
        if (item.children) {
          const children = item.children.filter(child => {
            const key = getNavKeyForHref(child.href)
            return Boolean(key && canAccess(key))
          })
          if (children.length === 0) return null
          if (!itemKey || !canAccess(itemKey)) {
            return null
          }
          return { ...item, children }
        }
        if (!itemKey || !canAccess(itemKey)) return null
        return item
      })
      .filter((item): item is NavItemConfig => item !== null)
  }, [navItems, canAccess, isReady])

  const userAvatar = user?.avatar
  const userInitial = user?.name?.charAt(0).toUpperCase() || 'U'

  const isActive = (href: string, exact?: boolean) => {
    if (exact) return location.pathname === href
    return location.pathname.startsWith(href)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className={cn(
      'sticky top-0 z-30 h-14 flex-shrink-0',
      'bg-card border-b border-border',
      'relative',
      className
    )}>
      <div className="h-full px-4 sm:px-6 flex items-center">

        <button
          type="button"
          onClick={onMenuOpen}
          className="lg:hidden p-2 -ml-1 rounded-md text-accent hover:bg-accent/5 transition-colors"
          aria-label="Abrir menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img src="/logo.svg" alt="ambcar" className="h-6 w-auto object-contain" />
          <span
            className="text-sm font-bold text-foreground tracking-tight"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
          >
            ambcar
          </span>
        </Link>

        <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-0.5">
          {filteredNavItems.map((item) => {
            if (item.children) {
              return (
                <NavDropdown
                  key={item.href}
                  item={item}
                  isActive={item.children.some((child) =>
                    isDropdownChildActive(location.pathname, child.href, child.matchNested)
                  )}
                  location={location}
                  navigate={navigate}
                />
              )
            }
            const active = isActive(item.href, item.exact)
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 flex-shrink-0',
                  active
                    ? 'text-accent bg-accent/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-surface'
                )}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="flex items-center gap-1 ml-auto">

          <button
            type="button"
            onClick={toggleTheme}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-surface transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
          >
            {theme === 'dark'
              ? <Sun className="h-[1.125rem] w-[1.125rem]" />
              : <Moon className="h-[1.125rem] w-[1.125rem]" />
            }
          </button>

          <LanguageSwitcher />

          <NotificationBell />

          <div className="relative ml-1" ref={profileRef}>
            <button
              type="button"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-150"
            >
              <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center flex-shrink-0 overflow-hidden ring-1 ring-border/60">
                {userAvatar ? (
                  <img className="w-7 h-7 rounded-full object-cover" src={userAvatar} alt={user?.name || 'Profile'} />
                ) : (
                  <span className="text-xs font-semibold text-white">{userInitial}</span>
                )}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-semibold text-foreground leading-tight max-w-[120px] truncate">
                  {user?.name}
                </p>
                <p className="text-[0.625rem] text-muted-foreground leading-tight capitalize">
                  {user?.dealershipRole}
                </p>
              </div>
              <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-card rounded-lg shadow-precision-lg border border-border z-50 animate-scale-in overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {userAvatar ? (
                      <img className="w-10 h-10 rounded-full object-cover" src={userAvatar} alt={user?.name || 'Profile'} />
                    ) : (
                      <span className="text-sm font-semibold text-white">{userInitial}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 capitalize truncate">{user?.dealershipRole}</p>
                  </div>
                </div>
                <div className="py-1">
                  {[
                    { to: '/profile', label: t('nav.profile'), key: 'nav.profile' },
                    { to: '/users', label: t('nav.users'), key: 'nav.users' },
                    { to: '/settings', label: t('nav.settings'), key: 'nav.settings.general' },
                  ]
                    .filter(link => canAccess(link.key))
                    .map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className="block px-4 py-2 text-sm text-foreground hover:bg-surface transition-colors duration-100"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      {label}
                    </Link>
                  ))}
                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      type="button"
                      onClick={() => { logout(); setIsProfileOpen(false) }}
                      className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors duration-100"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

interface NavDropdownProps {
  item: NavItemConfig
  isActive: boolean
  location: ReturnType<typeof useLocation>
  navigate: ReturnType<typeof useNavigate>
}

const NavDropdown: React.FC<NavDropdownProps> = ({ item, isActive, location, navigate }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const wideMenu = item.href === '/financial' || item.href === '/sales' || item.href === '/post-sales/pipeline'

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150',
          isActive
            ? 'text-accent bg-accent/10'
            : 'text-muted-foreground hover:text-foreground hover:bg-surface'
        )}
      >
        {item.label}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-150', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 top-full mt-1.5 bg-card rounded-lg shadow-precision-lg border border-border z-50 animate-scale-in overflow-hidden py-1 max-h-[min(24rem,calc(100vh-5rem))] overflow-y-auto',
            wideMenu ? 'w-56' : 'w-48'
          )}
        >
          {item.children!.map(({ label, href, matchNested }) => (
            <button
              key={href}
              type="button"
              onClick={() => { navigate(href); setOpen(false) }}
              className={cn(
                'w-full text-left px-4 py-2 text-sm transition-colors duration-100',
                isDropdownChildActive(location.pathname, href, matchNested)
                  ? 'text-accent bg-accent/5'
                  : 'text-foreground hover:bg-surface'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
